/**
 * Left odds panel — Backend-UI `apl-market-runner` clone.
 * Match: name + exposure + 3 back / 3 lay cells (Lab outer→best / best→outer).
 * Bookmaker: best back + best lay (Lab horseBack / horseLay).
 * Fancy: No|Yes header strip + session line with lay/back rates.
 * Betfair book rows live in BookTree (Lab work-station.html NE panel).
 */

import { useSession } from '@/api/hooks/shared/useSession'
import { useNetExposureDisplayCurrency } from '@/features/net-exposure/useNetExposureDisplayCurrency'
import { lineSessionMoneyOpts } from '@/features/net-exposure/lineSessionDisplayMoney'
import { money, odds } from '@/lib/format'
import { oddsOverlayLabel } from '@/api/map/rates'
import { isLineSessionMarket } from '@/api/map/workstation'
import type { BookType } from '../fixture'
import {
  runnerAmountsForBook,
  treeNetExposure,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationOddLevel,
  type WorkstationRunner,
} from '../workstation-fixture'
import { visibleMatchRunners } from './visibleRunners'

function toneClass(value: number): string {
  return value < 0 ? 'w7-ws-amt-neg' : 'w7-ws-amt-pos'
}

export function formatExposure(
  value: number,
  bookType: BookType,
  currency: string,
  alreadyDisplay = false,
): string {
  const body = money(value, { round: true, symbol: false, currency, alreadyDisplay })
  if (currency !== 'INR') return `${body} Pts`
  if (bookType !== 'My PT') return body
  return `Rs.${body}`
}

function ExposureAmt({
  value,
  bookType,
  marketType,
  lineSession = false,
}: {
  value: number
  bookType: BookType
  marketType: WorkstationMarketType
  lineSession?: boolean
}) {
  const { data } = useSession()
  const bookCurrency = useNetExposureDisplayCurrency(marketType)
  const lsOpts = lineSessionMoneyOpts(data?.account.currencyType)
  if (lineSession) {
    return <>{formatExposure(value, bookType, lsOpts.currency, lsOpts.alreadyDisplay)}</>
  }
  return <>{formatExposure(value, bookType, bookCurrency)}</>
}

function OddCell({
  side,
  level,
  main,
}: {
  side: 'back' | 'lay'
  level: WorkstationOddLevel | undefined
  main?: boolean
}) {
  const price = level && level.price > 0 ? odds(level.price) : ''
  const size = level && level.size > 0 ? String(Math.round(level.size)) : ''
  return (
    <div className={`w7-ws-odd w7-ws-odd-${side}${main ? ' w7-ws-odd-main' : ''}`}>
      <span className="w7-ws-odd-price">{price}</span>
      {size ? <span className="w7-ws-odd-size">{size}</span> : null}
    </div>
  )
}

function FancyOddCell({
  side,
  level,
}: {
  side: 'back' | 'lay'
  level: WorkstationOddLevel | undefined
}) {
  // Lab session strip: large run line (horseLay/horseBack) over rate (rateLay/rateBack).
  const runs =
    level && level.price > 0
      ? Number.isInteger(level.price)
        ? String(level.price)
        : String(level.price)
      : ''
  const rate = level && level.size > 0 ? String(Math.round(level.size)) : ''
  return (
    <div className={`w7-ws-odd w7-ws-odd-${side} w7-ws-odd-main w7-ws-odd-fancy`}>
      <span className="w7-ws-odd-price">{runs}</span>
      {rate ? <span className="w7-ws-odd-size">{rate}</span> : null}
    </div>
  )
}

function OddsOverlay({ status }: { status: WorkstationBook['status'] }) {
  const label = oddsOverlayLabel(status)
  if (!label) return null
  return (
    <div className="w7-ws-odds-overlay" role="status">
      {label}
    </div>
  )
}

/** Backend stores back outer→best as indices 2,1,0 visually left→right. */
function MatchRunnerRow({
  runner,
  amount,
  bookType,
  status,
}: {
  runner: WorkstationRunner
  amount: number
  bookType: BookType
  status: WorkstationBook['status']
}) {
  const back = runner.back
  const lay = runner.lay
  return (
    <div className="w7-ws-runner-row">
      <div className="w7-ws-runner-info">
        <span className="w7-ws-runner-name">{runner.name}</span>
        <span className={`w7-ws-runner-exp ${toneClass(amount)}`}>
          <ExposureAmt value={amount} bookType={bookType} marketType="match" />
        </span>
      </div>
      <div className="w7-ws-odds-strip">
        <OddsOverlay status={status} />
        {/* Outer → best back, then best → outer lay (Backend-UI cell order) */}
        <OddCell side="back" level={back[2]} />
        <OddCell side="back" level={back[1]} />
        <OddCell side="back" level={back[0]} main />
        <OddCell side="lay" level={lay[0]} main />
        <OddCell side="lay" level={lay[1]} />
        <OddCell side="lay" level={lay[2]} />
      </div>
    </div>
  )
}

/** Lab bookmaker: two cells — horseBack (best back) + horseLay (best lay). */
function BookmakerRunnerRow({
  runner,
  amount,
  bookType,
  status,
}: {
  runner: WorkstationRunner
  amount: number
  bookType: BookType
  status: WorkstationBook['status']
}) {
  return (
    <div className="w7-ws-runner-row">
      <div className="w7-ws-runner-info">
        <span className="w7-ws-runner-name">{runner.name}</span>
        <span className={`w7-ws-runner-exp ${toneClass(amount)}`}>
          <ExposureAmt value={amount} bookType={bookType} marketType="bookmaker" />
        </span>
      </div>
      <div className="w7-ws-odds-strip w7-ws-odds-strip-fancy">
        <OddsOverlay status={status} />
        <OddCell side="back" level={runner.back[0]} main />
        <OddCell side="lay" level={runner.lay[0]} main />
      </div>
    </div>
  )
}

function FancyPanel({ book, bookType }: { book: WorkstationBook; bookType: BookType }) {
  // Prefer Lab ladder NE on the tree — empty/placeholder runners must not paint Rs.0.
  // Lab: No = horseLay on `no` (or single-line lay[0]), Yes = horseBack on `yes` (or back[0]).
  const lineSession = isLineSessionMarket({
    marketId: book.marketId,
    marketName: book.marketName,
    lineLadder: book.lineLadder,
  })
  const fancyNe = treeNetExposure(book.tree, 'fancy', bookType)
  const no = book.runners.find((r) => {
    const id = r.id.trim().toLowerCase()
    const name = r.name.trim().toLowerCase()
    return id === 'no' || name === 'no'
  })
  const yes = book.runners.find((r) => {
    const id = r.id.trim().toLowerCase()
    const name = r.name.trim().toLowerCase()
    return id === 'yes' || name === 'yes'
  })
  const line = no ?? yes ?? book.runners[0]
  const lay = no?.lay[0] ?? line?.lay[0]
  const back = yes?.back[0] ?? line?.back[0]
  return (
    <div className="w7-ws-fancy">
      <div className="w7-ws-runner-row w7-ws-fancy-labels">
        <div className="w7-ws-runner-info" />
        <div className="w7-ws-odds-strip w7-ws-odds-strip-fancy">
          <div className="w7-ws-odd w7-ws-odd-label">
            <span className="w7-ws-odd-price">No</span>
          </div>
          <div className="w7-ws-odd w7-ws-odd-label">
            <span className="w7-ws-odd-price">Yes</span>
          </div>
        </div>
      </div>
      <div className="w7-ws-runner-row">
        <div className="w7-ws-runner-info">
          <span className="w7-ws-runner-name">{book.marketName}</span>
          <span className={`w7-ws-runner-exp ${toneClass(fancyNe)}`}>
            <ExposureAmt
              value={fancyNe}
              bookType={bookType}
              marketType="fancy"
              lineSession={lineSession}
            />
          </span>
        </div>
        <div className="w7-ws-odds-strip w7-ws-odds-strip-fancy">
          <OddsOverlay status={book.status} />
          <FancyOddCell side="lay" level={lay} />
          <FancyOddCell side="back" level={back} />
        </div>
      </div>
    </div>
  )
}

export function OddsPanel({
  book,
  bookType,
}: {
  book: WorkstationBook
  bookType: BookType
}) {
  if (book.type === 'fancy') {
    return <FancyPanel book={book} bookType={bookType} />
  }

  const amounts = runnerAmountsForBook(book, bookType)
  const amountsByRunnerId = new Map(book.runners.map((runner, i) => [runner.id, amounts[i] ?? 0]))
  const runners = book.type === 'match' ? visibleMatchRunners(book.runners) : book.runners

  if (book.type === 'bookmaker') {
    return (
      <div className="w7-ws-market-runners" data-status={book.status}>
        {runners.map((runner) => (
          <BookmakerRunnerRow
            key={runner.id}
            runner={runner}
            amount={amountsByRunnerId.get(runner.id) ?? 0}
            bookType={bookType}
            status={book.status}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="w7-ws-market-runners" data-status={book.status}>
      {runners.map((runner) => (
        <MatchRunnerRow
          key={runner.id}
          runner={runner}
          amount={amountsByRunnerId.get(runner.id) ?? 0}
          bookType={bookType}
          status={book.status}
        />
      ))}
    </div>
  )
}
